function isPromise<U>(value: U | Promise<U>): value is Promise<U> {
    return value instanceof Promise;
}

export abstract class Option<T> {
    abstract isDefined(): boolean;
    abstract get(): T;

    isEmpty(): boolean {
        return !this.isDefined();
    }

    getOrElse(defaultValue: T): T {
        return this.isDefined() ? this.get() : defaultValue;
    }

    map<U>(fn: (value: T) => U): Option<U>;
    map<U>(fn: (value: T) => Promise<U>): Option<Promise<U>>;
    map<U>(fn: (value: T) => U | Promise<U>): Option<U> | Option<Promise<U>> {
        if (!this.isDefined()) {
            return Option.none<U>();
        }

        const result = fn(this.get());
        if (isPromise(result)) {
            return Option.some(result) as Option<Promise<U>>;
        }

        return Option.some(result) as Option<U>;
    }

    exists(predicate: (value: T) => boolean): boolean {
        return this.isDefined() && predicate(this.get());
    }

    contains(value: T): boolean {
        return this.exists(v => v === value);
    }

    flatMap<U>(fn: (value: T) => Option<U>): Option<U>;
    flatMap<U>(fn: (value: T) => Promise<Option<U>>): Promise<Option<U>>;
    flatMap<U>(fn: (value: T) => Option<U> | Promise<Option<U>>): Option<U> | Promise<Option<U>> {
        if (!this.isDefined()) {
            return Option.none<U>();
        }

        const result = fn(this.get());
        if (isPromise(result)) {
            return result;
        }

        return result;
    }

    filter(predicate: (value: T) => boolean): Option<T> {
        return this.isDefined() && predicate(this.get()) ? this : Option.none<T>();
    }

    match<U>(onNone: () => U, onSome: (value: T) => U): U {
        return this.isDefined() ? onSome(this.get()) : onNone();
    }

    fold<U>(ifEmpty: () => U, fn: (value: T) => U): U {
        return this.isDefined() ? fn(this.get()) : ifEmpty();
    }

    tap(fn: (value: T) => void): Option<T> {
        if (this.isDefined()) {
            fn(this.get());
        }
        return this;
    }

    toNull(): T | null {
        return this.fold(
            () => null,
            value => value,
        );
    }

    static none<T>(): Option<T> {
        return new None<T>();
    }

    static some<T>(value: T | null | undefined): Option<T> {
        return value === null || value === undefined ? Option.none<T>() : new Some(value);
    }
}

class Some<T> extends Option<T> {
    constructor(private readonly value: T) {
        super();
    }

    isDefined(): boolean {
        return true;
    }

    get(): T {
        return this.value;
    }
}

class None<T> extends Option<T> {
    isDefined(): boolean {
        return false;
    }

    get(): T {
        throw new Error('No such element');
    }
}
